/**
 * The seam between a Worker response and a station screen's props.
 *
 * Each mapper is a pure function, which is the whole point: the screens are
 * already proved by stations.test.tsx against hand-built props, so what remains
 * unproved is whether the shape the API actually returns becomes those props. That
 * is a data question, not a rendering one, and it is tested here without a DOM.
 *
 * Nothing in this file contains a business rule. Walk order arrives sorted from
 * `buildPickList`, box choice arrives decided from the packing domain, and a
 * mapper that recomputed either would be a second implementation of it.
 */
import type {
  CatalogueQueueResponse,
  ConsolidationResponse,
  ContestedClaimsResponse,
  PickListResponse,
  RolloutResponse,
} from '../data/endpoints'
import type { CatalogueUnit } from './IntakeCatalogue'
import type { PickLine } from './Picking'
import type { ClaimEvidence } from './Adjudication'
import type { AdminTile } from './AdminTiles'

/** The API may name a title `titleName` or `title` depending on the reader; take either. */
function titleOf(row: { titleName?: string; title?: string }): string {
  return row.titleName ?? row.title ?? 'Untitled'
}

export function toPickLines(res: PickListResponse): PickLine[] {
  return res.lines.map((l) => ({
    unitId: l.unitId,
    title: titleOf(l),
    binLabel: l.binLabel,
    walkSequence: l.walkSequence,
  }))
}

/** Packing works one parcel at a time; the consolidation groups pick which one. */
export function toPackingRemaining(res: ConsolidationResponse, orderId: string): Array<{ unitId: string; title: string }> {
  const group = res.groups.find((g) => g.orderId === orderId)
  return (group?.units ?? []).map((u) => ({ unitId: u.unitId, title: titleOf(u) }))
}

export function packingOrderRef(res: ConsolidationResponse, orderId: string): string {
  const group = res.groups.find((g) => g.orderId === orderId)
  return group?.orderNo ?? orderId
}

export function toCatalogueUnit(res: CatalogueQueueResponse): CatalogueUnit | null {
  const first = res.items[0]
  if (!first) return null
  return {
    id: first.unitId,
    title: titleOf(first),
    format: first.format ?? 'unknown',
    ageGroup: first.ageGroup ?? 'unset',
    grade: first.grade ?? 'B',
    priceInPaise: first.pricePaise ?? 0,
    defects: first.defects ?? [],
  }
}

export interface ContestedUnit {
  unit: { unitId: string; title: string }
  claims: ClaimEvidence[]
}

export function toContestedUnits(res: ContestedClaimsResponse): ContestedUnit[] {
  return res.items.map((item) => ({
    unit: { unitId: item.dropItemId, title: titleOf(item) },
    claims: item.claims.map((c) => ({
      claimId: c.claimId,
      claimant: c.claimantName ?? c.claimant ?? 'Unknown',
      group: c.groupName ?? c.group ?? 'Unknown group',
      claimedAt: c.claimedAt,
      rank: c.rank,
    })),
  }))
}

/**
 * The admin home counts three queues, and the verb on each tile is the action it
 * leads to rather than the noun it holds - "resolve", not "manual entries".
 */
export function toAdminTiles(counts: { manualEntry: number; assistedOutbox: number; shipmentRetries: number }): AdminTile[] {
  return [
    { id: 'manual-entry', label: 'Manual entry', count: counts.manualEntry, verb: 'Resolve' },
    { id: 'assisted-outbox', label: 'Messages to send', count: counts.assistedOutbox, verb: 'Present' },
    { id: 'shipment-retries', label: 'Shipments to retry', count: counts.shipmentRetries, verb: 'Retry' },
  ]
}

export interface RolloutRow {
  name: string
  role: string
  features: string[]
}

/** Per person, alphabetical, so Mitali / Mary / Jayant read in a stable order. */
export function toRolloutRows(res: RolloutResponse): RolloutRow[] {
  return [...res.people]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((p) => ({ name: p.name, role: p.role, features: [...p.features].sort() }))
}
